import { useImperativeHandle, useRef, type Ref } from 'react'
import { LazyLocationCell } from 'app/components/LazyLocationCell'
import NameIcon from 'app/components/shared/NameIcon'
import { Button } from 'app/components/ui/button'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { initials } from 'app/lib/contactQuickEntry'
import type { ContactJson } from 'app/types/entities'

export type ContactSuggestionListHandle = {
  /** Move keyboard focus onto the first row (the heading's ArrowDown). */
  focusFirst: () => void
}

type Props = {
  contacts: ContactJson[]
  onOpen: (contact: ContactJson) => void
  /** ArrowUp from the first row: hand focus back to the heading. */
  onLeaveUp: () => void
  ref?: Ref<ContactSuggestionListHandle>
}

/**
 * "Already a contact?" rows under the heading. Arrow keys walk the rows, Enter or
 * click opens one, ArrowUp from the top returns to the heading.
 */
export function ContactSuggestionList({ contacts, onOpen, onLeaveUp, ref }: Props) {
  const listRef = useRef<HTMLDivElement>(null)
  const rows = () => [...(listRef.current?.querySelectorAll<HTMLButtonElement>(`button[data-suggestion]`) ?? [])]
  const focusRow = (index: number) => {
    const all = rows()
    all[Math.max(0, Math.min(index, all.length - 1))]?.focus()
  }
  useImperativeHandle(ref, () => ({ focusFirst: () => focusRow(0) }), [])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== `ArrowDown` && e.key !== `ArrowUp`) return
    e.preventDefault()
    const current = rows().indexOf(document.activeElement as HTMLButtonElement)
    if (e.key === `ArrowUp` && current === 0) {
      onLeaveUp()
      return
    }
    focusRow(current + (e.key === `ArrowDown` ? 1 : -1))
  }

  if (contacts.length === 0) return null
  return (
    <div ref={listRef} onKeyDown={onKeyDown} className="rounded-lg border border-default bg-surface-subtle">
      <div className="px-3 pb-1 pt-2 text-body-xs text-subtle">Already a contact?</div>
      {contacts.map((contact) => (
        <Button
          key={contact.id}
          type="button"
          variant="ghost"
          size="row"
          data-suggestion
          onClick={() => onOpen(contact)}
        >
          <NameIcon fullName={initials(contact.name)} heightClassName="h-6" widthClassName="w-6" isTiny />
          <span className="min-w-0 flex-1 truncate text-ui-sm text-strong">{contact.name}</span>
          <span className="flex min-w-0 items-center gap-2 text-body-xs text-subtle">
            {contact.cellPhone && <span>{formatPhoneNumber(contact.cellPhone)}</span>}
            {contact.locationId && <LazyLocationCell locationId={contact.locationId} className="truncate" />}
          </span>
        </Button>
      ))}
    </div>
  )
}
